// Story 6.35 (ADO-1693, FR90) — per-tenant white-label brand theming, web side.
//
// The catalogue and shell are already 100% design-token driven (tokens.css,
// Story 6.9), so a brand is applied at the app ROOT and the whole app inherits
// it — no component changes. The Phase-1 production mechanism is a single
// predefined-brand selector:
//   `dataBrand` sets `<html data-brand="…">`, selecting a pre-defined, AA-vetted
//   `[data-brand="…"]` token block in tokens.css (architecture.md:797). A tenant
//   is themed by assignment to a predefined brand set (e.g. LSN) plus a swappable
//   `logoUrl` — no code deploy (FR90).
//
// The `headerBg` / `headerDarkBg` / `accent` fields are a DORMANT Phase-2 seam:
// arbitrary per-tenant chrome colours are deferred (manager decision) and are
// NOT applied here. An inline colour would bypass the tokens.css WCAG-AA contrast
// gate (tokens.contrast.spec.ts, AC4), so lighting this up in Phase 2 must go
// behind a runtime contrast guard. The fields stay on the type as the seam; see
// docs/tenancy/tenant-onboarding.md ("arbitrary per-tenant colours are Phase-2").
//
// This module mirrors the shape of `@shoplink/tenant-context` `BrandConfig`; the
// web cannot import that barrel (it re-exports NestJS `@Module()` decorators —
// see `auth/rbac.ts`), so the type lives here, web-safe.

/**
 * Design-system brand keys that ship a predefined, AA-vetted `[data-brand="…"]`
 * token block in tokens.css. A `dataBrand` outside this set names no block, so it
 * is ignored (the tenant renders the default brand) rather than silently emitting
 * a dead attribute — keep this in lockstep with the `[data-brand='…']` blocks.
 */
const KNOWN_DATA_BRANDS: ReadonlySet<string> = new Set(['lsn']);

/**
 * `dataBrand` values that explicitly name the DEFAULT ShopLink (BWG) brand. The
 * default is the bare `:root` palette and ships no `[data-brand]` block, so these
 * resolve to `null` — the default brand — with no dead attribute and no dev
 * warning. This keeps the onboarding doc's `dataBrand: "bwg"` guidance
 * (docs/tenancy/tenant-onboarding.md) in lockstep with the code.
 */
const DEFAULT_BRAND_ALIASES: ReadonlySet<string> = new Set(['bwg']);

/** The resolved, default-filled brand the SPA renders. */
export interface WebBrandConfig {
  /** Brand name; the logo's accessible text. */
  readonly name: string;
  /** `[data-brand]` key, or `null` for the default ShopLink brand. */
  readonly dataBrand: string | null;
  /** Logo asset URL (app-relative or absolute). */
  readonly logoUrl: string;
  /**
   * Whether this tenant carries its own visual brand identity — a predefined
   * brand palette (`dataBrand`) OR a custom `logoUrl`. Gates the white brand
   * header (Story 6.35 design update, ADO-1693): a themed tenant lifts its logo
   * into a white bar above the coloured nav bar and shows the account badge
   * there; an unthemed tenant (default ShopLink, e.g. no brand config on the
   * wire) keeps the logo in the coloured bar with no white header. Derived in
   * {@link resolveBrand}; never read from the wire.
   */
  readonly themed: boolean;
  /**
   * DORMANT Phase-2 seam — arbitrary per-tenant chrome-colour overrides. Present
   * on the type, but NOT resolved from the wire and NOT applied in Phase 1 (an
   * inline colour would bypass the AA contrast gate — see module header); always
   * `null` today.
   */
  readonly headerBg: string | null;
  readonly headerDarkBg: string | null;
  readonly accent: string | null;
}

/** The default ShopLink (BWG) brand — used whenever no brand config is present. */
export const DEFAULT_BRAND: WebBrandConfig = {
  name: 'ShopLink',
  dataBrand: null,
  logoUrl: '/images/logo.svg',
  themed: false,
  headerBg: null,
  headerDarkBg: null,
  accent: null,
};

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * A `dataBrand` value, but only when it names a predefined brand block; otherwise
 * `null` (the default brand). A value that explicitly names the default brand
 * (e.g. `"bwg"`) also resolves to `null` — the default ships no `[data-brand]`
 * block. An unrecognised key — typically a tenant-onboarding typo — would
 * otherwise silently render the default palette with no signal, so it is
 * surfaced in dev.
 */
function knownDataBrand(value: string | null): string | null {
  if (value === null) return null;
  // Explicit default-brand alias: the default has no [data-brand] block, so map
  // it to null rather than a dead attribute — and do not dev-warn (it is valid).
  if (DEFAULT_BRAND_ALIASES.has(value)) return null;
  if (KNOWN_DATA_BRANDS.has(value)) return value;
  if (import.meta.env.DEV) {
    // apps/web is exempt from the no-console rule (project-context.md logging).
    console.warn(
      `[brand] Unrecognised dataBrand "${value}" — no [data-brand="${value}"] token block ships; rendering the default brand.`,
    );
  }
  return null;
}

/**
 * Normalise the untyped wire `brandConfig` into a fully-resolved
 * {@link WebBrandConfig}, filling every default. Pure — never touches the DOM.
 * `null`/malformed input yields {@link DEFAULT_BRAND}. The Phase-2 colour-override
 * fields are intentionally NOT read from the wire (they stay `null`, dormant) —
 * see the module header.
 */
export function resolveBrand(raw: unknown): WebBrandConfig {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return DEFAULT_BRAND;
  const r = raw as Record<string, unknown>;
  const dataBrand = knownDataBrand(str(r['dataBrand']));
  const logoUrl = str(r['logoUrl']) ?? DEFAULT_BRAND.logoUrl;
  return {
    name: str(r['name']) ?? DEFAULT_BRAND.name,
    dataBrand,
    logoUrl,
    // Themed when the tenant carries its own visual identity: a predefined brand
    // palette (dataBrand) OR a custom logo. The default-brand alias "bwg" resolves
    // dataBrand to null, so a BWG Foodservice tenant is themed purely by its custom
    // logoUrl (it renders the default blue palette) — see the white-header gate in
    // SiteHeader. A config with neither is the default ShopLink brand (unthemed).
    themed: dataBrand !== null || logoUrl !== DEFAULT_BRAND.logoUrl,
    headerBg: null,
    headerDarkBg: null,
    accent: null,
  };
}

/**
 * Apply a resolved brand to the document root (default `document.documentElement`).
 * Phase 1 applies only the predefined-brand selector: it sets `data-brand` for a
 * known brand and clears it for the default, so no stale brand survives a tenant
 * change or logout. The dormant Phase-2 colour overrides are intentionally not
 * written here — an inline custom property would bypass the tokens.css AA gate.
 */
export function applyBrandTheme(
  brand: WebBrandConfig,
  root: HTMLElement = document.documentElement,
): void {
  if (brand.dataBrand !== null) {
    root.setAttribute('data-brand', brand.dataBrand);
  } else {
    root.removeAttribute('data-brand');
  }
}
